import React, { useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Input, Label, Spinner } from "reactstrap";
import { useLocation } from "react-router-dom";
import { Module, PageActionInputItem } from "../types";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('role_management');

interface ModuleNode extends Module {
  children: ModuleNode[];
}

interface ModuleCheckboxListProps {
  modules: Module[];
  selectedModuleIds: number[];
  onChange: (moduleIds: number[]) => void;
  disabled?: boolean;
  /** Khi truyền vào, hiển thị thêm nút "Xem action/input" cho các module có Url (trang thật) — dùng ở popup cập nhật permission */
  onViewActions?: (module: Module) => void;
  /** Module đang được xem action/input, dùng để highlight và mở khu vực action/input ngay dưới node đó */
  activeModuleId?: number | null;
  /** Danh sách action/input của module đang active — hiển thị inline ngay dưới node, chia 2 nhóm Button/Input */
  actionInputs?: PageActionInputItem[];
  actionInputsLoading?: boolean;
  selectedActionInputIds?: number[];
  onToggleActionInput?: (id: number) => void;
}

const buildModuleTree = (modules: Module[]): ModuleNode[] => {
  const nodeMap = new Map<number, ModuleNode>();
  modules.forEach((module) =>
    nodeMap.set(module.id, { ...module, children: [] }),
  );

  const roots: ModuleNode[] = [];
  nodeMap.forEach((node) => {
    if (node.parentId && nodeMap.has(node.parentId)) {
      nodeMap.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortRecursive = (nodes: ModuleNode[]) => {
    nodes.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    nodes.forEach((node) => sortRecursive(node.children));
  };
  sortRecursive(roots);

  return roots;
};

const ModuleCheckboxList: React.FC<ModuleCheckboxListProps> = ({
  modules,
  selectedModuleIds,
  onChange,
  disabled,
  onViewActions,
  activeModuleId,
  actionInputs,
  actionInputsLoading,
  selectedActionInputIds,
  onToggleActionInput,
}) => {
  const location = useLocation();
  const tree = useMemo(() => buildModuleTree(modules), [modules]);
  const [expandedModuleIds, setExpandedModuleIds] = useState<Set<number>>(
    new Set(),
  );
  // Nhóm "Thao tác button" / "Enable nhập dữ liệu" cũng là 1 node cây có thể mở/thu gọn riêng,
  // giống hệt module — reset lại mỗi khi đổi sang xem module khác.
  const [expandedActionGroups, setExpandedActionGroups] = useState<
    Set<"Button" | "Input">
  >(new Set());

  useEffect(() => {
    setExpandedActionGroups(new Set());
  }, [activeModuleId]);
  const moduleById = useMemo(
    () => new Map(modules.map((module) => [module.id, module])),
    [modules],
  );
  const childrenByParent = useMemo(() => {
    const map = new Map<number, Module[]>();
    modules.forEach((module) => {
      if (!module.parentId) return;
      map.set(module.parentId, [...(map.get(module.parentId) || []), module]);
    });
    return map;
  }, [modules]);

  const getDescendantIds = (moduleId: number): number[] => {
    const children = childrenByParent.get(moduleId) || [];
    return children.flatMap((child) => [
      child.id,
      ...getDescendantIds(child.id),
    ]);
  };

  const getAncestorIds = (moduleId: number): number[] => {
    const module = moduleById.get(moduleId);
    if (!module?.parentId) return [];
    return [module.parentId, ...getAncestorIds(module.parentId)];
  };

  // Tự động mở rộng (expand) các node cha chứa module đã được gán (checked) — để khi mở popup
  // Cập Nhật Permission, user thấy ngay các module đã gán mà không phải tự bấm mở từng cấp.
  // Chỉ merge thêm (không tự thu gọn), nên không ghi đè thao tác thu gọn thủ công của user.
  useEffect(() => {
    if (selectedModuleIds.length === 0) return;
    setExpandedModuleIds((prev) => {
      const next = new Set(prev);
      selectedModuleIds.forEach((id) => {
        getAncestorIds(id).forEach((ancestorId) => next.add(ancestorId));
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuleIds]);

  const handleModuleToggle = (moduleId: number) => {
    const selected = new Set(selectedModuleIds);
    const willSelect = !selected.has(moduleId);
    const relatedIds = [moduleId, ...getDescendantIds(moduleId)];

    // Chọn cha -> tự động chọn hết các cấp con; bỏ chọn cha -> tự động bỏ chọn hết cấp con.
    if (willSelect) {
      relatedIds.forEach((id) => selected.add(id));
    } else {
      relatedIds.forEach((id) => selected.delete(id));
    }

    // Đồng bộ trạng thái cha theo con trực tiếp: cha tự động được chọn khi TẤT CẢ con
    // trực tiếp đã chọn, và tự động bỏ chọn khi có con chưa chọn — lan truyền dần lên gốc.
    let ancestorId = moduleById.get(moduleId)?.parentId ?? null;
    while (ancestorId != null) {
      const directChildren = childrenByParent.get(ancestorId) || [];
      const allChildrenSelected =
        directChildren.length > 0 &&
        directChildren.every((child) => selected.has(child.id));

      if (allChildrenSelected) {
        selected.add(ancestorId);
      } else {
        selected.delete(ancestorId);
      }

      ancestorId = moduleById.get(ancestorId)?.parentId ?? null;
    }

    onChange(Array.from(selected));
  };

  const handleSelectAll = () => {
    onChange(
      selectedModuleIds.length === modules.length
        ? []
        : modules.map((module) => module.id),
    );
  };

  const toggleExpanded = (moduleId: number) => {
    setExpandedModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  const toggleActionGroup = (groupKey: "Button" | "Input") => {
    setExpandedActionGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // Node lá (1 action-button/input cụ thể) — cùng style box với module, chọn bằng checkbox.
  const renderActionLeafNode = (item: PageActionInputItem, depth: number) => {
    const isChecked = selectedActionInputIds?.includes(item.id) ?? false;
    return (
      <div
        key={item.id}
        className="border rounded px-3 py-2 mb-2"
        style={{
          marginLeft: depth * 24,
          backgroundColor: isChecked ? "#f0f9ff" : "white",
          borderColor: isChecked ? "#3b82f6" : "#e5e7eb",
        }}
      >
        <FormGroup check className="mb-0">
          <Input
            type="checkbox"
            id={`action-input-${item.id}`}
            checked={isChecked}
            data-action-code="input-page-action-input-checkbox"
            data-action-name="Chọn action/input"
            onChange={() => onToggleActionInput?.(item.id)}
            disabled={
              disabled ||
              !PageActionPermission.canEnable(
                location.pathname,
                "input-page-action-input-checkbox",
              )
            }
          />
          <Label
            check
            htmlFor={`action-input-${item.id}`}
            className="ms-2"
            style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          >
            <i className="ri-file-list-3-line text-muted me-2"></i>
            {item.name} <small className="text-muted">({item.code})</small>
          </Label>
        </FormGroup>
      </div>
    );
  };

  // Node nhóm ("Thao tác button" / "Enable nhập dữ liệu") — cùng style box + expand arrow với module,
  // bấm vào mới load ra các phần tử con bên trong, giữ đúng cảm giác treeview thống nhất.
  const renderActionGroupNode = (
    groupKey: "Button" | "Input",
    title: string,
    items: PageActionInputItem[],
    depth: number,
  ) => {
    if (items.length === 0) return null;
    const isExpanded = expandedActionGroups.has(groupKey);

    return (
      <div key={groupKey}>
        <div
          className="border rounded px-3 py-2 mb-2"
          style={{
            marginLeft: depth * 24,
            backgroundColor: "white",
            borderColor: "#e5e7eb",
          }}
        >
          <div className="d-flex align-items-start">
            <Button
              color="light"
              size="sm"
              className="btn-icon flex-shrink-0 me-2"
              title={isExpanded ? st('thu_gon_text') : st('mo_rong_text')}
              onClick={() => toggleActionGroup(groupKey)}
            >
              <i
                className={
                  isExpanded ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"
                }
              ></i>
            </Button>
            <div className="flex-grow-1 d-flex align-items-center">
              <i className="ri-folder-3-line text-warning me-2 fs-5"></i>
              <strong>{title}</strong>
              <span className="text-muted ms-2 small">{items.length} {st('muc_text')}</span>
            </div>
          </div>
        </div>
        {isExpanded &&
          items.map((item) => renderActionLeafNode(item, depth + 1))}
      </div>
    );
  };

  const renderNode = (node: ModuleNode, depth = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedModuleIds.has(node.id);
    const isSelected = selectedModuleIds.includes(node.id);
    const childIds = getDescendantIds(node.id);
    const selectedChildren = childIds.filter((id) =>
      selectedModuleIds.includes(id),
    ).length;
    const isIndeterminate = !isSelected && selectedChildren > 0;

    const isActiveForActions = activeModuleId === node.id;

    return (
      <div key={node.id}>
        <div
          className="border rounded px-3 py-2 mb-2"
          style={{
            marginLeft: depth * 24,
            backgroundColor: isSelected ? "#f0f9ff" : "white",
            borderColor: isActiveForActions
              ? "#10b981"
              : isSelected || isIndeterminate
                ? "#3b82f6"
                : "#e5e7eb",
            borderWidth: isActiveForActions ? 2 : 1,
          }}
        >
          <div className="d-flex align-items-start">
            <Button
              color="light"
              size="sm"
              className="btn-icon flex-shrink-0 me-2"
              disabled={!hasChildren || disabled}
              title={
                hasChildren
                  ? isExpanded
                    ? st('thu_gon_text')
                    : st('mo_rong_text')
                  : st('khong_co_menu_con_text')
              }
              onClick={() => hasChildren && toggleExpanded(node.id)}
            >
              <i
                className={
                  hasChildren
                    ? isExpanded
                      ? "ri-arrow-down-s-line"
                      : "ri-arrow-right-s-line"
                    : "ri-subtract-line"
                }
              ></i>
            </Button>
            <FormGroup check className="mb-0 flex-grow-1">
              <Input
                type="checkbox"
                id={`module-${node.id}`}
                checked={isSelected}
                data-action-code="input-module-checkbox"
                data-action-name="Chọn module"
                onChange={() => handleModuleToggle(node.id)}
                disabled={
                  disabled ||
                  !PageActionPermission.canEnable(
                    location.pathname,
                    "input-module-checkbox",
                  )
                }
              />
              <Label
                check
                htmlFor={`module-${node.id}`}
                className="d-flex align-items-start ms-2"
                style={{ cursor: disabled ? "not-allowed" : "pointer" }}
              >
                <i
                  className={`${hasChildren ? "ri-folder-3-line text-warning" : "ri-file-list-3-line text-muted"} me-2 fs-5`}
                  style={{ minWidth: "24px" }}
                ></i>
                {node.icon && (
                  <i
                    className={`${node.icon} me-2 fs-5`}
                    style={{ minWidth: "24px" }}
                  ></i>
                )}
                <div className="flex-grow-1">
                  <strong className="d-block">
                    {node.name}
                    {isIndeterminate && (
                      <span className="text-primary ms-2 small">
                        {st('da_chon_menu_con_text')}
                      </span>
                    )}
                  </strong>
                  {node.description && (
                    <small className="text-muted d-block">
                      {node.description}
                    </small>
                  )}
                  <small className="text-muted">
                    {node.permissionCount} {st('quyen_hint')}
                    {hasChildren ? `, ${node.children.length} menu con` : ""}
                  </small>
                </div>
              </Label>
            </FormGroup>
            {onViewActions &&
              node.url &&
              PageActionPermission.canDisplay(
                location.pathname,
                "button-view-page-action-inputs",
              ) && (
                <Button
                  color={isActiveForActions ? "success" : "light"}
                  size="sm"
                  className="btn-icon flex-shrink-0 ms-2"
                  title={st('xem_action_input_cua_trang_nay_title')}
                  data-action-code="button-view-page-action-inputs"
                  data-action-name="Xem action/input theo module"
                  onClick={() => onViewActions(node)}
                >
                  <i className="ri-list-check-2"></i>
                </Button>
              )}
          </div>
        </div>
        {isActiveForActions && onViewActions && (
          <>
            {actionInputsLoading ? (
              <div
                className="text-muted small py-2"
                style={{ marginLeft: (depth + 1) * 24 }}
              >
                <Spinner size="sm" className="me-2" />
                {st('dang_tai_action_input_text')}
              </div>
            ) : actionInputs && actionInputs.length > 0 ? (
              <>
                {renderActionGroupNode(
                  "Button",
                  st('thao_tac_text'),
                  actionInputs.filter((i) => i.type !== "Input"),
                  depth + 1,
                )}
                {renderActionGroupNode(
                  "Input",
                  st('truong_du_lieu_text'),
                  actionInputs.filter((i) => i.type === "Input"),
                  depth + 1,
                )}
              </>
            ) : (
              <div
                className="text-muted small py-2"
                style={{ marginLeft: (depth + 1) * 24 }}
              >
                {st('trang_nay_chua_co_action_input_text')}
              </div>
            )}
          </>
        )}
        {hasChildren &&
          isExpanded &&
          node.children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  // const orderedModules = flattenByHierarchy(selectableModules);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Label className="mb-0">{st('phan_quyen_danh_muc_label')}</Label>
        {PageActionPermission.canDisplay(
          location.pathname,
          "button-toggle-select-all-modules",
        ) && (
          <Button
            color="link"
            size="sm"
            data-action-code="button-toggle-select-all-modules"
            data-action-name="Chọn/Bỏ chọn tất cả module"
            onClick={handleSelectAll}
            disabled={disabled}
            className="text-decoration-none"
          >
            {selectedModuleIds.length === modules.length
              ? st('bo_chon_tat_ca_text')
              : st('chon_tat_ca_text')}
          </Button>
        )}
      </div>

      <div>{tree.map((node) => renderNode(node))}</div>

      {selectedModuleIds.length > 0 && (
        <div className="alert alert-info mt-3 mb-0">
          <i className="ri-information-line me-2"></i>
          {st('da_chon_text')} <strong>{selectedModuleIds.length}</strong> {st('module_text')}
        </div>
      )}
    </div>
  );
};

export default ModuleCheckboxList;
